/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { buildConversationEndpointUrl } from 'src/core/utils/endpointBuilder/buildConversationEndpointUrl';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AzureCsAccountCard } from 'src/luis/appShell/managePane/azureAccountsPage/components/AzureCsAccountCard';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { useAssignedAzureAccounts } from 'src/modules/azure/hooks/useAssignedAzureAccounts';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

const messages = defineMessages({
    buttonLabel: {
        id: 'azure.predictionResourcesPivot.buttonLabel',
        defaultMessage: 'Add prediction resource'
    },
    emptyCardsPlaceholder: {
        id: 'azure.predictionResourcesPivot.emptyCardsPlaceholder',
        defaultMessage: `There are no prediction resources assigned.
Click on the "Add prediction resource" button to assign one to the application.`
    },
    addingPredictionResourceFailed: {
        id: 'azure.predictionResourcesPivot.addingPredictionResourceFailed',
        defaultMessage: 'Failed to add the prediction resource.'
    },
    addingPredictionResourceLoading: {
        id: 'azure.predictionResourcesPivot.addingPredictionResourceLoading',
        defaultMessage: 'Adding the prediction resource...'
    },
    addingPredictionResourceCompleted: {
        id: 'azure.predictionResourcesPivot.addingPredictionResourceCompleted',
        defaultMessage: 'The prediction resource added.'
    },
    unassignPredictionResourceFailed: {
        id: 'azure.predictionResourcesPivot.unassignPredictionResourceFailed',
        defaultMessage: 'Failed to unassign the prediction resource.'
    },
    unassignPredictionResourceLoading: {
        id: 'azure.predictionResourcesPivot.unassignPredictionResourceLoading',
        defaultMessage: 'Un-assigning the prediction resource...'
    },
    unassignPredictionResourceCompleted: {
        id: 'azure.predictionResourcesPivot.unassignPredictionResourceCompleted',
        defaultMessage: 'The prediction resource unassigned.'
    },
    internalId: {
        id: 'azure.predictionResourcesPivot.id',
        defaultMessage: 'Id'
    },
    noAccessWarning: {
        id: 'azure.predictionResourcesPivot.noAccessWarning',
        defaultMessage: 'This app has { count } collaborator resource(s) you do not have permission to view.'
    },
    unAssignKeyLabel: {
        id: 'azure.predictionResourcesPivot.unAssignKeyLabel',
        defaultMessage: 'Un-assign key'
    },
    learnMoreDetailed: {
        id: 'azure.predictionResourcesPivot.learnMoreDetailed',
        defaultMessage: 'Learn more about how to assign prediction resources to your app.'
    }
});

const { styleComponent } = getStylist('PredictionResourcesPivot');

export const Section = styleComponent<IStackProps>(Stack)('Section', theme => ({ paddingTop: theme.sectionPaddingTop }));
export const FullWidthContainer = styleComponent<IStackProps>(Stack)('FullWidthContainer', theme => ({
    width: theme.fullWidthContainerWidth
}));
const EmptyPlaceholder = styleComponent<LuisTextProps>(LuisText)('EmptyPlaceholder', theme => ({ color: theme.emptyPlaceholderColor }));

const assignResourcesLearnMoreUrl =
    'https://docs.microsoft.com/azure/cognitive-services/luis/luis-how-to-azure-subscription#assign-a-resource-to-an-app';

/**
 * The component displaying the prediction resources
 * inside the azure resources page of a LUIS app
 */
export const PredictionResourcesPivot = observer(() => {
    const { localize } = useLocalization();
    const [isAzureCsAccountSelectModalOpen, setIsAzureCsAccountSelectModalOpen] = React.useState(false);
    const { dispatcher, rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canManageAzureResoruces } = mapUserRoleToUserActions(userRole);

    const { assignedAzureAccounts, isLoading } = useAssignedAzureAccounts();
    const collaboratorAssignedResources = rootStore.currentLuisAppStore.azureAccounts.filter(
        a => a.path === LuisConstants.Azure.CollaboratorKeyPath
    );

    /**
     * Assigns the given key to the application.
     *
     * @param csAccount The account to add to the application.
     */
    const onAssignCsAccount = (csAccount: AzureCsAccountStore) => {
        dispatcher.dispatch('assignAppAzureCsAccount', {
            ...csAccount,
            toastData: {
                failed: { title: localize(messages.addingPredictionResourceFailed) },
                running: { title: localize(messages.addingPredictionResourceLoading) },
                completed: { title: localize(messages.addingPredictionResourceCompleted) }
            }
        });
        setIsAzureCsAccountSelectModalOpen(false);
    };

    const onUnAssignCsAccount = (id: string) => {
        dispatcher.dispatch('unAssignAppAzureCsAccount', {
            id,
            toastData: {
                failed: { title: localize(messages.unassignPredictionResourceFailed) },
                running: { title: localize(messages.unassignPredictionResourceLoading) },
                completed: { title: localize(messages.unassignPredictionResourceCompleted) }
            }
        });
    };

    return (
        <Section tokens={{ childrenGap: 25 }} horizontalAlign="start">
            <RBACTooltip
                hidden={canManageAzureResoruces}
                displayedUserRole={displayedUserRole}
                componentSurroundedByToolTip={
                    <PrimaryButton
                        text={localize(messages.buttonLabel)}
                        onClick={() => setIsAzureCsAccountSelectModalOpen(true)}
                        disabled={!canManageAzureResoruces}
                    />
                }
            />

            {collaboratorAssignedResources.length && (
                <FullWidthContainer tokens={{ childrenGap: 25 }}>
                    <MessageBar messageBarType={MessageBarType.info}>
                        {localize(messages.noAccessWarning, { count: collaboratorAssignedResources.length })}
                    </MessageBar>

                    {collaboratorAssignedResources.map(r => (
                        <LuisText variant="small">
                            {localize(messages.internalId)}:{r.id}
                            &nbsp;
                            <Link onClick={() => onUnAssignCsAccount(r.id)}>({localize(messages.unAssignKeyLabel)})</Link>
                        </LuisText>
                    ))}

                    <LuisSeparator />
                </FullWidthContainer>
            )}

            {isLoading && <ShimmerFactory includeHeaderShimmer shimmerCount={5} shimmerSpacing={20} />}

            {assignedAzureAccounts.map(resource => (
                <FullWidthContainer tokens={{ childrenGap: 25 }} key={getId()}>
                    <AzureCsAccountCard
                        showUnAssignButton
                        azureCsAccount={resource}
                        onUnAssignButtonClick={onUnAssignCsAccount}
                        buildEndpointUrl={buildConversationEndpointUrl}
                    />

                    <LuisSeparator />
                </FullWidthContainer>
            ))}

            {!isLoading && !assignedAzureAccounts.length && (
                <EmptyPlaceholder>
                    {localize(messages.emptyCardsPlaceholder)}
                    &nbsp;
                    <ExternalLink aria-label={localize(messages.learnMoreDetailed)} href={assignResourcesLearnMoreUrl}>
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </EmptyPlaceholder>
            )}

            <AzureResourcesManager
                serviceTarget="LUIS"
                onSelectResource={onAssignCsAccount}
                isSelectResourceVisible={isAzureCsAccountSelectModalOpen}
                onChangeSelectResourceVisible={setIsAzureCsAccountSelectModalOpen}
                strategies={{
                    resource: r =>
                        AzureResourcesStrategies.luisAppPredictionStrategy(
                            r,
                            rootStore.azureStore.session.csAccountStore,
                            assignedAzureAccounts
                        )
                }}
            />
        </Section>
    );
});
